import React, { useState, useRef, useEffect } from "react";
import { Sparkles, Send, Lightbulb, AlertTriangle } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints, streamChat } from "@/lib/api";
import { Panel, PageHeader, StatusBadge } from "@/components/common";
import { inr } from "@/lib/format";
import { renderMarkdown } from "@/lib/markdown";

const SUGGESTIONS = [
  "Why did utility cost change today?",
  "What is my biggest opportunity to cut ₹/litre?",
  "Explain the current refrigeration performance.",
  "Where am I losing the most money right now?",
];

export default function AIOperations() {
  const { data } = usePoll(endpoints.enterprise, 6000);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`sess-${Math.random().toString(36).slice(2, 10)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    try {
      await streamChat(
        sessionRef.current,
        q,
        (delta) => setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + delta };
          return copy;
        }),
        (sid) => { sessionRef.current = sid; }
      );
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "_Sorry, I hit an error reaching the AI service._" };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ai-operations-page">
      <PageHeader icon={Sparkles} title="AI Operations Center"
        subtitle="Ask the dairy anything — grounded on live twin & economics data. AI Powered." />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <Panel testid="ai-chat-panel" className="flex flex-col" >
            <div ref={scrollRef} className="h-[440px] overflow-y-auto space-y-3 pr-1" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center text-slate-400">
                  <Sparkles size={28} className="text-violet-300 mb-2" />
                  <p className="text-sm">Ask about cost, losses, anomalies or opportunities.</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-700 border border-slate-100"}`}>
                    {m.role === "assistant"
                      ? (m.text ? <div className="[&_strong]:text-slate-900 [&_h2]:font-semibold [&_li]:my-0.5" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} /> : <div className="flex gap-1 items-center h-5 px-1"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "0ms"}}></span><span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "150ms"}}></span><span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "300ms"}}></span></div>)
                      : m.text}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-1.5 mt-3 mb-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} disabled={busy} data-testid="chat-suggestion"
                  className="text-[11px] px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600 transition-colors disabled:opacity-50">
                  {s}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask the Ergoniq AI…"
                className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
              <button onClick={() => send()} disabled={busy} data-testid="chat-send"
                className="px-4 bg-slate-900 text-white rounded-md hover:bg-slate-700 disabled:opacity-60 transition-colors flex items-center gap-1.5 text-sm font-semibold">
                <Send size={15} /> Send
              </button>
            </div>
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel testid="ai-opportunities" title="Top Opportunities">
            <div className="space-y-2">
              {(data?.opportunities || []).slice(0, 5).map((o, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <Lightbulb size={14} className="text-violet-500 mt-0.5 shrink-0" />
                  <span className="flex-1 text-slate-700 leading-tight">{o.title}</span>
                  <span className="font-mono font-semibold text-violet-600 text-xs whitespace-nowrap">{inr(o.value_per_year)}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel testid="ai-anomalies" title="Active Anomalies">
            {(data?.anomalies || []).length === 0 ? (
              <div className="text-sm text-slate-400 py-4 text-center">None active ✓</div>
            ) : (
              <div className="space-y-2">
                {data.anomalies.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <AlertTriangle size={14} className="text-red-500 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-slate-700 leading-tight">{a.label}</div>
                      <StatusBadge status={a.severity} />
                    </div>
                    <span className="font-mono font-semibold text-red-600 text-xs whitespace-nowrap">{inr(a.impact_per_year)}/yr</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
